import { camelizeKeys } from '@agently/utils';
import axios, { AxiosError } from 'axios';
import { decamelizeKeys } from 'fast-case';
import qs from 'query-string';

import { getAuthToken, handleUnauthorized } from '../auth-token';

export const ASSISTANT_URL = import.meta.env.VITE_ASSISTANT_URL;

export const isAssistantEnabled = Boolean(ASSISTANT_URL);

/** Entries to the Assistant show only for an enabled team and with VITE_ASSISTANT_URL. */
export const canUseAssistant = (team: { assistantEnabled: boolean }) =>
  isAssistantEnabled && team.assistantEnabled;

const defaultTransformers = <T>(transform?: T | T[]): T[] => {
  if (!transform) {
    return [];
  }

  return Array.isArray(transform) ? transform : [transform];
};

// No global error toasts: callers handle `AssistantError`s themselves.
export const assistantApi = axios.create({
  baseURL: ASSISTANT_URL,
  paramsSerializer: {
    serialize: (params) =>
      qs.stringify(decamelizeKeys(params), {
        skipEmptyString: true,
        skipNull: true,
      }),
  },
  transformRequest: [
    (data: any): unknown => decamelizeKeys(data),
    ...defaultTransformers(axios.defaults.transformRequest),
  ],
  transformResponse: [
    ...defaultTransformers(axios.defaults.transformResponse),
    (data: any): unknown => camelizeKeys(data),
  ],
});

assistantApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

assistantApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  },
);
